import { openDB, IDBPDatabase } from 'idb';
import { generateUUID } from '../lib/uuid';
import { supabase } from '../lib/supabase';
import { loadActiveTenant } from '../lib/tenantStorage';

// Define the database schema interface
interface RestaurantDB {
  customers: {
    key: string; // UUID
    value: {
      tenant_id: string;
      id: string;
      name: string;
      phone: string;
      notes?: string;
      created_at: string;
      updated_at: string;
    };
    indexes: {
      'by-phone': string;
      'by-name': string;
      'by-created': string;
    };
  };
  tables: {
    key: string; // UUID
    value: {
      tenant_id: string;
      id: string;
      number: number;
      seats: number;
      zone: string;
      status: 'active' | 'inactive' | 'maintenance';
      created_at: string;
      updated_at: string;
      floor_plan_x_pct?: number | null;
      floor_plan_y_pct?: number | null;
      /** Visual shape on the floor-plan canvas: round | square | rect */
      floor_plan_shape?: string | null;
      /** Room/zone on the floor-plan canvas, e.g. main | roof | lounge */
      floor_plan_room_id?: string | null;
    };
    indexes: {
      'by-number': number;
      'by-zone': string;
      'by-status': string;
    };
  };
  shifts: {
    key: string; // UUID
    value: {
      tenant_id: string;
      id: string;
      name: string;
      start_time: string;
      end_time: string;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    indexes: {
      'by-name': string;
      'by-active': boolean;
    };
  };
  reservations: {
    key: string; // UUID
    value: {
      tenant_id: string;
      id: string;
      customer_id: string;
      table_id?: string;
      table_ids?: string[];
      shift_id: string;
      date: string;
      time: string;
      party_size: number;
      status: 'confirmed' | 'in-progress' | 'cancelled' | 'completed' | 'no-show';
      source?: string;
      notes?: string;
      seated_at?: string; // ISO timestamp when guest was seated
      created_at: string;
      updated_at: string;
    };
    indexes: {
      'by-date': string;
      'by-customer': string;
      'by-table': string;
      'by-shift': string;
      'by-status': string;
      'by-date-time': string;
    };
  };
  table_groups: {
    key: string; // UUID
    value: {
      tenant_id: string;
      id: string;
      anchor_table_id: string;
      table_ids: string[];
      room_id?: string;
      label?: string;
      status: 'active' | 'split';
      created_at: string;
      updated_at: string;
    };
    indexes: {
      'by-status': string;
      'by-anchor': string;
    };
  };

  sync_queue: {
    key: string; // UUID
    value: {
      tenant_id: string;
      id: string;
      table_name: string;
      record_id: string;
      operation: 'INSERT' | 'UPDATE' | 'DELETE';
      data?: any;
      device_id?: string;
      created_at: string;
      processed_at?: string;
      status: 'pending' | 'processing' | 'completed' | 'failed';
    };
    indexes: {
      'by-status': string;
      'by-created': string;
      'by-table': string;
    };
  };
}

class LocalDatabaseService {
  private db: IDBPDatabase | null = null;
  private readonly BASE_DB_NAME = 'restaurant_local_db';
  private currentTenantId: string | null = null;
  private readonly DB_VERSION = 4; // Adds table_groups store

  private getDbName(): string {
    return this.currentTenantId ? `${this.BASE_DB_NAME}_${this.currentTenantId}` : this.BASE_DB_NAME;
  }

  private getTenantIdOrThrow(): string {
    if (!this.currentTenantId) {
      throw new Error('Tenant context is not set for local database');
    }
    return this.currentTenantId;
  }

  isInitialized(): boolean {
    return this.db !== null;
  }

  async setTenantContext(tenantId: string | null): Promise<void> {
    const previousTenantId = this.currentTenantId;
    this.currentTenantId = tenantId;
    
    // If switching to a different tenant, clear the old database connection
    // and reinitialize for the new tenant
    if (this.db) {
      await this.closeDatabase();
    }
    
    // Always clear the legacy "no-tenant" database to prevent cross-restaurant bleed
    // if an old browser tab had cached data without a tenant suffix.
    if (tenantId) {
      await this.deleteDatabase(this.BASE_DB_NAME).catch(() => {
        // Non-fatal; just a safety cleanup
      });
    }
    
    // Only initialize if we have a tenantId
    if (tenantId) {
      await this.initialize();
      
      // If we switched tenants (not just initializing), log it for debugging
      if (previousTenantId && previousTenantId !== tenantId) {
        console.log(`🔄 Switched tenant context from ${previousTenantId} to ${tenantId}`);
      }
    }
  }

  async initialize(): Promise<void> {
    try {
      // Resolve tenant from active session if not already set
      if (!this.currentTenantId) {
        const { data } = await supabase.auth.getSession();
        const userId = data.session?.user?.id;
        if (userId) {
          this.currentTenantId = loadActiveTenant(userId);
        }
      }

      this.db = await openDB(this.getDbName(), this.DB_VERSION, {
        upgrade(db, oldVersion, _newVersion, transaction) {
          // Version 1 -> 2: Remove unique constraint from tables.by-number index
          if (oldVersion >= 1 && oldVersion < 2 && db.objectStoreNames.contains('tables')) {
            const tablesStore = transaction.objectStore('tables');
            if (tablesStore.indexNames.contains('by-number')) {
              tablesStore.deleteIndex('by-number');
              tablesStore.createIndex('by-number', 'number'); // Recreate without unique
            }
          }

          // Version 2 -> 3: Remove unique constraint from customers.by-phone index
          if (oldVersion >= 1 && oldVersion < 3 && db.objectStoreNames.contains('customers')) {
            const customersStore = transaction.objectStore('customers');
            if (customersStore.indexNames.contains('by-phone')) {
              customersStore.deleteIndex('by-phone');
              customersStore.createIndex('by-phone', 'phone'); // Recreate without unique
            }
          }

          // Version 3 -> 4: Add temporary table groups store
          if (oldVersion >= 1 && oldVersion < 4 && !db.objectStoreNames.contains('table_groups')) {
            const tableGroupsStore = db.createObjectStore('table_groups', { keyPath: 'id' });
            tableGroupsStore.createIndex('by-status', 'status');
            tableGroupsStore.createIndex('by-anchor', 'anchor_table_id');
          }

          // Initial schema creation (version 0 -> 1 or fresh install)
          if (oldVersion < 1) {
            // Create customers store
            const customersStore = db.createObjectStore('customers', { keyPath: 'id' });
            customersStore.createIndex('by-phone', 'phone'); // Not unique - allow duplicates from Supabase
            customersStore.createIndex('by-name', 'name');
            customersStore.createIndex('by-created', 'created_at');

            // Create tables store
            const tablesStore = db.createObjectStore('tables', { keyPath: 'id' });
            tablesStore.createIndex('by-number', 'number'); // Not unique - different zones can have same number
            tablesStore.createIndex('by-zone', 'zone');
            tablesStore.createIndex('by-status', 'status');

            // Create shifts store
            const shiftsStore = db.createObjectStore('shifts', { keyPath: 'id' });
            shiftsStore.createIndex('by-name', 'name');
            shiftsStore.createIndex('by-active', 'is_active');

            // Create reservations store
            const reservationsStore = db.createObjectStore('reservations', { keyPath: 'id' });
            reservationsStore.createIndex('by-date', 'date');
            reservationsStore.createIndex('by-customer', 'customer_id');
            reservationsStore.createIndex('by-table', 'table_id');
            reservationsStore.createIndex('by-shift', 'shift_id');
            reservationsStore.createIndex('by-status', 'status');
            reservationsStore.createIndex('by-date-time', ['date', 'time']);

            // Create temporary table groups store
            const tableGroupsStore = db.createObjectStore('table_groups', { keyPath: 'id' });
            tableGroupsStore.createIndex('by-status', 'status');
            tableGroupsStore.createIndex('by-anchor', 'anchor_table_id');

            // Create sync queue store
            const syncQueueStore = db.createObjectStore('sync_queue', { keyPath: 'id' });
            syncQueueStore.createIndex('by-status', 'status');
            syncQueueStore.createIndex('by-created', 'created_at');
            syncQueueStore.createIndex('by-table', 'table_name');
          }
        },
      });

      console.log('✅ Local database initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize local database:', error);
      throw error;
    }
  }

  // Customer operations
  async createCustomer(customer: Omit<RestaurantDB['customers']['value'], 'id' | 'created_at' | 'updated_at'>, skipSync = false): Promise<string> {
    if (!this.db) throw new Error('Database not initialized');
    const tenantId = this.getTenantIdOrThrow();
    
    const id = generateUUID();
    const now = new Date().toISOString();
    const customerData = {
      ...customer,
      tenant_id: tenantId,
      id,
      created_at: now,
      updated_at: now,
    };

    await this.db.add('customers', customerData);
    
    // Add to sync queue (unless skipped for seeding)
    if (!skipSync) {
      await this.addToSyncQueue('customers', id, 'INSERT', customerData);
    }
    
    return id;
  }

  async getCustomer(id: string): Promise<RestaurantDB['customers']['value'] | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('customers', id);
  }

  async getCustomerByPhone(phone: string): Promise<RestaurantDB['customers']['value'] | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getFromIndex('customers', 'by-phone', phone);
  }

  /**
   * Remap a customer ID to a new ID and update all local references (customers, reservations, sync queue).
   * Used when a duplicate customer (same phone, different id) collides with an existing remote record.
   */
  async remapCustomerId(oldId: string, newId: string, customerData?: RestaurantDB['customers']['value']): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    if (oldId === newId) return;

    const tx = this.db.transaction(['customers', 'reservations', 'sync_queue'], 'readwrite');
    const customersStore = tx.objectStore('customers');
    const reservationsStore = tx.objectStore('reservations');
    const syncStore = tx.objectStore('sync_queue');

    // Move customer record
    const existingCustomer = await customersStore.get(oldId);
    if (existingCustomer) {
      await customersStore.delete(oldId);
    }
    if (customerData) {
      await customersStore.put({ ...customerData, id: newId });
    } else if (existingCustomer) {
      await customersStore.put({ ...existingCustomer, id: newId });
    }

    // Update reservations that reference the old customer id
    const reservationsWithOld = await reservationsStore.index('by-customer').getAll(oldId);
    for (const reservation of reservationsWithOld) {
      await reservationsStore.put({
        ...reservation,
        customer_id: newId,
        updated_at: new Date().toISOString(),
      });
    }

    // Update pending sync items that reference the old id
    const allSyncItems = await syncStore.getAll();
    for (const item of allSyncItems) {
      let updated = false;
      const newItem = { ...item };
      if (item.record_id === oldId && item.table_name === 'customers') {
        newItem.record_id = newId;
        if (newItem.data?.id) {
          newItem.data = { ...newItem.data, id: newId };
        }
        updated = true;
      }
      if (item.table_name === 'reservations' && item.data?.customer_id === oldId) {
        newItem.data = { ...newItem.data, customer_id: newId };
        updated = true;
      }
      if (updated) {
        await syncStore.put(newItem);
      }
    }

    await tx.done;
  }

  /**
   * Remap a table ID to a new ID and update all local references (tables, reservations, sync queue).
   * Used when a duplicate table (same number, different id) collides with an existing remote record.
   */
  async remapTableId(oldId: string, newId: string, tableData?: RestaurantDB['tables']['value']): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    if (oldId === newId) return;

    const tx = this.db.transaction(['tables', 'reservations', 'sync_queue'], 'readwrite');
    const tablesStore = tx.objectStore('tables');
    const reservationsStore = tx.objectStore('reservations');
    const syncStore = tx.objectStore('sync_queue');

    // Move table record
    const existingTable = await tablesStore.get(oldId);
    if (existingTable) {
      await tablesStore.delete(oldId);
    }
    if (tableData) {
      await tablesStore.put({ ...tableData, id: newId });
    } else if (existingTable) {
      await tablesStore.put({ ...existingTable, id: newId });
    }

    // Update reservations that reference the old table id
    const reservationsWithOld = await reservationsStore.index('by-table').getAll(oldId);
    for (const reservation of reservationsWithOld) {
      await reservationsStore.put({
        ...reservation,
        table_id: newId,
        table_ids: Array.isArray(reservation.table_ids)
          ? reservation.table_ids.map((tableId: string) => (tableId === oldId ? newId : tableId))
          : [newId],
        updated_at: new Date().toISOString(),
      });
    }

    // Update pending sync items that reference the old id
    const allSyncItems = await syncStore.getAll();
    for (const item of allSyncItems) {
      let updated = false;
      const newItem = { ...item };
      if (item.record_id === oldId && item.table_name === 'tables') {
        newItem.record_id = newId;
        if (newItem.data?.id) {
          newItem.data = { ...newItem.data, id: newId };
        }
        updated = true;
      }
      if (item.table_name === 'reservations' && item.data?.table_id === oldId) {
        newItem.data = {
          ...newItem.data,
          table_id: newId,
          table_ids: Array.isArray(newItem.data?.table_ids)
            ? newItem.data.table_ids.map((tableId: string) => (tableId === oldId ? newId : tableId))
            : [newId],
        };
        updated = true;
      }
      if (updated) {
        await syncStore.put(newItem);
      }
    }

    await tx.done;
  }

  async updateCustomer(id: string, updates: Partial<RestaurantDB['customers']['value']>): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const customer = await this.db.get('customers', id);
    if (!customer) throw new Error('Customer not found');

    const updatedCustomer = {
      ...customer,
      ...updates,
      updated_at: new Date().toISOString(),
    };

    await this.db.put('customers', updatedCustomer);
    
    // Add to sync queue
    await this.addToSyncQueue('customers', id, 'UPDATE', updatedCustomer);
  }

  async deleteCustomer(id: string, skipSync = false): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    // Get customer data before deletion for sync queue
    const customer = await this.db.get('customers', id);
    
    await this.db.delete('customers', id);
    
    if (customer && !skipSync) {
      await this.addToSyncQueue('customers', id, 'DELETE');
    } else {
      // Even with skipSync, purge any leftover pending INSERT/UPDATE entries
      // so that pushPendingToSupabase doesn't re-insert a customer we just deleted.
      await this.clearPendingSyncForRecord('customers', id);
    }
  }

  async getAllCustomers(): Promise<RestaurantDB['customers']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAll('customers');
  }

  // Table operations
  async createTable(table: Omit<RestaurantDB['tables']['value'], 'id' | 'created_at' | 'updated_at'>, skipSync = false): Promise<string> {
    if (!this.db) throw new Error('Database not initialized');
    const tenantId = this.getTenantIdOrThrow();
    
    const id = generateUUID();
    const now = new Date().toISOString();
    const tableData = {
      ...table,
      tenant_id: tenantId,
      id,
      created_at: now,
      updated_at: now,
    };

    await this.db.add('tables', tableData);
    
    // Add to sync queue (unless skipped for seeding)
    if (!skipSync) {
      await this.addToSyncQueue('tables', id, 'INSERT', tableData);
    }
    
    return id;
  }

  async getTable(id: string): Promise<RestaurantDB['tables']['value'] | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('tables', id);
  }

  async getAllTables(): Promise<RestaurantDB['tables']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAll('tables');
  }

  async getTablesByZone(zone: string): Promise<RestaurantDB['tables']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAllFromIndex('tables', 'by-zone', zone);
  }

  async updateTable(id: string, updates: Partial<RestaurantDB['tables']['value']>): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const table = await this.db.get('tables', id);
    if (!table) throw new Error('Table not found');

    const updatedTable = {
      ...table,
      ...updates,
      updated_at: new Date().toISOString(),
    };

    await this.db.put('tables', updatedTable);
    
    // Add to sync queue
    await this.addToSyncQueue('tables', id, 'UPDATE', updatedTable);
  }

  async deleteTable(id: string, skipSync = false): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const table = await this.db.get('tables', id);

    await this.db.delete('tables', id);

    if (table && !skipSync) {
      await this.addToSyncQueue('tables', id, 'DELETE');
    } else {
      // Purge any stale pending INSERT/UPDATE entries so pushPendingToSupabase
      // doesn't re-insert a table that was already deleted remotely.
      await this.clearPendingSyncForRecord('tables', id);
    }
  }

  // Table group operations (temporary operational merge/split)
  async createTableGroup(data: {
    table_ids: string[];
    anchor_table_id?: string;
    room_id?: string;
    label?: string;
  }): Promise<string> {
    if (!this.db) throw new Error('Database not initialized');
    const tenantId = this.getTenantIdOrThrow();

    const deduped = Array.from(new Set(data.table_ids));
    if (deduped.length < 2) {
      throw new Error('At least two distinct tables are required to merge');
    }

    const now = new Date().toISOString();
    const id = generateUUID();
    const group = {
      tenant_id: tenantId,
      id,
      anchor_table_id: data.anchor_table_id && deduped.includes(data.anchor_table_id)
        ? data.anchor_table_id
        : deduped[0],
      table_ids: deduped,
      room_id: data.room_id,
      label: data.label,
      status: 'active' as const,
      created_at: now,
      updated_at: now,
    };

    await this.db.put('table_groups', group);
    await this.addToSyncQueue('table_groups', id, 'INSERT', group);
    return id;
  }

  async getAllTableGroups(): Promise<RestaurantDB['table_groups']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAll('table_groups');
  }

  async getActiveTableGroups(): Promise<RestaurantDB['table_groups']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAllFromIndex('table_groups', 'by-status', 'active');
  }

  async getTableGroup(id: string): Promise<RestaurantDB['table_groups']['value'] | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('table_groups', id);
  }

  async updateTableGroup(id: string, updates: Partial<RestaurantDB['table_groups']['value']>): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    const existing = await this.db.get('table_groups', id);
    if (!existing) throw new Error('Table group not found');
    const merged = {
      ...existing,
      ...updates,
      updated_at: new Date().toISOString(),
    };
    await this.db.put('table_groups', merged);
    await this.addToSyncQueue('table_groups', id, 'UPDATE', merged);
  }

  async splitTableGroup(id: string): Promise<void> {
    await this.updateTableGroup(id, { status: 'split' });
  }

  // Shift operations
  async createShift(shift: Omit<RestaurantDB['shifts']['value'], 'id' | 'created_at' | 'updated_at'>, skipSync = false): Promise<string> {
    if (!this.db) throw new Error('Database not initialized');
    const tenantId = this.getTenantIdOrThrow();
    
    const id = generateUUID();
    const now = new Date().toISOString();
    const shiftData = {
      ...shift,
      tenant_id: tenantId,
      id,
      created_at: now,
      updated_at: now,
    };

    await this.db.add('shifts', shiftData);
    if (!skipSync) {
      await this.addToSyncQueue('shifts', id, 'INSERT', shiftData);
    }
    return id;
  }

  async getShift(id: string): Promise<RestaurantDB['shifts']['value'] | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('shifts', id);
  }

  async getAllShifts(): Promise<RestaurantDB['shifts']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAll('shifts');
  }

  async getActiveShifts(): Promise<RestaurantDB['shifts']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAllFromIndex('shifts', 'by-active', true as any);
  }

  async updateShift(id: string, updates: Partial<RestaurantDB['shifts']['value']>): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const shift = await this.db.get('shifts', id);
    if (!shift) throw new Error('Shift not found');

    const updatedShift = {
      ...shift,
      ...updates,
      updated_at: new Date().toISOString(),
    };

    await this.db.put('shifts', updatedShift);
    await this.addToSyncQueue('shifts', id, 'UPDATE', updatedShift);
  }

  async deleteShift(id: string, skipSync = false): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const shift = await this.db.get('shifts', id);

    await this.db.delete('shifts', id);

    if (shift && !skipSync) {
      await this.addToSyncQueue('shifts', id, 'DELETE');
    } else {
      // Purge any stale pending INSERT/UPDATE entries so pushPendingToSupabase
      // doesn't re-insert a shift that was already deleted remotely.
      await this.clearPendingSyncForRecord('shifts', id);
    }
  }

  // Reservation operations
  async createReservation(reservation: Omit<RestaurantDB['reservations']['value'], 'id' | 'created_at' | 'updated_at'>): Promise<string> {
    if (!this.db) throw new Error('Database not initialized');
    const tenantId = this.getTenantIdOrThrow();
    
    const id = generateUUID();
    const now = new Date().toISOString();
    const reservationData = {
      ...reservation,
      table_ids: Array.isArray(reservation.table_ids)
        ? reservation.table_ids
        : reservation.table_id
          ? [reservation.table_id]
          : [],
      tenant_id: tenantId,
      id,
      created_at: now,
      updated_at: now,
    };

    console.log('✨ [LocalDB] Creating NEW reservation:', id, reservationData);
    console.trace('📍 [LocalDB] createReservation called from:');
    await this.db.add('reservations', reservationData);
    
    // Add to sync queue
    await this.addToSyncQueue('reservations', id, 'INSERT', reservationData);
    console.log('✅ [LocalDB] Reservation created successfully');
    
    return id;
  }

  /**
   * Insert a reservation directly without adding to sync queue
   * Used for realtime INSERT events from Supabase (already synced)
   */
  async insertReservationFromRealtime(reservation: RestaurantDB['reservations']['value']): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    if (!reservation.id) throw new Error('Reservation must have an ID');
    
    // Check if already exists
    const existing = await this.db.get('reservations', reservation.id);
    if (existing) {
      console.log(`⏭️ [LocalDB] Reservation ${reservation.id} already exists, skipping realtime insert`);
      return;
    }
    
    console.log(`✨ [LocalDB] Inserting reservation from realtime (no sync): ${reservation.id}`);
    await this.db.put('reservations', reservation);
  }

  async getReservation(id: string): Promise<RestaurantDB['reservations']['value'] | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('reservations', id);
  }

  async getReservationsByDate(date: string): Promise<RestaurantDB['reservations']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAllFromIndex('reservations', 'by-date', date);
  }

  async getReservationsByCustomer(customerId: string): Promise<RestaurantDB['reservations']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAllFromIndex('reservations', 'by-customer', customerId);
  }

  async updateReservation(id: string, updates: Partial<RestaurantDB['reservations']['value']>): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    console.log('📝 [LocalDB] Updating reservation:', id, updates);
    const reservation = await this.db.get('reservations', id);
    if (!reservation) {
      console.error('❌ [LocalDB] Reservation not found for update:', id);
      throw new Error('Reservation not found');
    }

    const updatedReservation = {
      ...reservation,
      ...updates,
      table_ids: Array.isArray(updates.table_ids)
        ? updates.table_ids
        : Object.prototype.hasOwnProperty.call(updates, 'table_id')
          ? (updates.table_id ? [updates.table_id] : [])
          : reservation.table_ids ?? (reservation.table_id ? [reservation.table_id] : []),
      updated_at: new Date().toISOString(),
    };

    console.log('💾 [LocalDB] Putting updated reservation to IndexedDB:', updatedReservation.id);
    await this.db.put('reservations', updatedReservation);
    
    // Add to sync queue
    await this.addToSyncQueue('reservations', id, 'UPDATE', updatedReservation);
    console.log('✅ [LocalDB] Reservation updated successfully');
  }

  async deleteReservation(id: string, skipSync = false): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    // Get reservation data before deletion for sync queue
    const reservation = await this.db.get('reservations', id);
    
    await this.db.delete('reservations', id);
    
    if (reservation && !skipSync) {
      await this.addToSyncQueue('reservations', id, 'DELETE');
    } else {
      // Purge any stale pending INSERT/UPDATE entries so pushPendingToSupabase
      // doesn't re-insert a reservation that was already deleted remotely.
      await this.clearPendingSyncForRecord('reservations', id);
    }
  }

  async getAllReservations(): Promise<RestaurantDB['reservations']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    const reservations = await this.db.getAll('reservations');
    console.log(`📋 [LocalDB] getAllReservations returned ${reservations.length} reservations`);
    
    // Check for duplicates
    const ids = reservations.map(r => r.id);
    const uniqueIds = new Set(ids);
    if (ids.length !== uniqueIds.size) {
      console.error('❌ [LocalDB] DUPLICATE IDs FOUND IN INDEXEDDB!', {
        total: ids.length,
        unique: uniqueIds.size,
        duplicates: ids.filter((id, index) => ids.indexOf(id) !== index)
      });
    }
    
    return reservations;
  }

  async bulkUpsertCustomers(customers: Array<RestaurantDB['customers']['value']>) {
    if (!this.db || !customers?.length) return;
    const skipIds = await this.getPendingRecordIds('customers');
    
    // First, get all existing customers to check for phone duplicates
    const allExisting = await this.getAllCustomers();
    const existingByPhone = new Map<string, RestaurantDB['customers']['value']>();
    for (const existing of allExisting) {
      if (existing.phone) {
        const normalizedPhone = existing.phone.trim();
        if (!existingByPhone.has(normalizedPhone)) {
          existingByPhone.set(normalizedPhone, existing);
        }
      }
    }
    
    const tx = this.db.transaction('customers', 'readwrite');
    const store = tx.objectStore('customers');

    // Build a map of phone -> customer to deduplicate incoming customers by phone number
    const incomingByPhone = new Map<string, RestaurantDB['customers']['value']>();
    
    // First pass: deduplicate incoming customers by phone (keep most recent)
    for (const customer of customers) {
      if (!customer?.id || skipIds.has(customer.id)) continue;
      if (!customer.phone) {
        // Customer without phone - just upsert by ID
        const existing = await store.get(customer.id);
        const record = {
          ...existing,
          ...customer,
          notes: customer.notes ?? existing?.notes ?? undefined,
          created_at: customer.created_at || existing?.created_at || new Date().toISOString(),
          updated_at: customer.updated_at || new Date().toISOString(),
        };
        await store.put(record);
        continue;
      }
      
      const normalizedPhone = customer.phone.trim();
      const existingIncoming = incomingByPhone.get(normalizedPhone);
      
      // Keep the customer with the most recent updated_at
      if (!existingIncoming || 
          (customer.updated_at && existingIncoming.updated_at && 
           customer.updated_at > existingIncoming.updated_at)) {
        incomingByPhone.set(normalizedPhone, customer);
      }
    }

    // Second pass: upsert deduplicated customers, handling phone duplicates
    for (const customer of incomingByPhone.values()) {
      const normalizedPhone = customer.phone.trim();
      const existingByPhoneCustomer = existingByPhone.get(normalizedPhone);
      
      // If there's an existing customer with this phone but different ID, delete the old one
      if (existingByPhoneCustomer && existingByPhoneCustomer.id !== customer.id) {
        await store.delete(existingByPhoneCustomer.id);
      }
      
      // Normal upsert
      const existingById = await store.get(customer.id);
      const record = {
        ...existingById,
        ...customer,
        notes: customer.notes ?? existingById?.notes ?? undefined,
        created_at: customer.created_at || existingById?.created_at || new Date().toISOString(),
        updated_at: customer.updated_at || new Date().toISOString(),
      };
      await store.put(record);
    }

    await tx.done;
  }

  async bulkUpsertTables(tables: Array<RestaurantDB['tables']['value']>) {
    if (!this.db || !tables?.length) return;
    const skipIds = await this.getPendingRecordIds('tables');
    const tx = this.db.transaction('tables', 'readwrite');
    const store = tx.objectStore('tables');

    for (const table of tables) {
      if (!table?.id || skipIds.has(table.id)) continue;
      const existing = await store.get(table.id);
      const status = table.status || existing?.status || 'available';
      const record = {
        ...existing,
        ...table,
        zone: table.zone || existing?.zone || 'Γενική',
        status,
        created_at: table.created_at || existing?.created_at || new Date().toISOString(),
        updated_at: table.updated_at || new Date().toISOString(),
      };
      await store.put(record as any);
    }

    await tx.done;
  }

  async bulkUpsertShifts(shifts: Array<RestaurantDB['shifts']['value']>) {
    if (!this.db || !shifts?.length) return;
    const skipIds = await this.getPendingRecordIds('shifts');
    const tx = this.db.transaction('shifts', 'readwrite');
    const store = tx.objectStore('shifts');

    for (const shift of shifts) {
      if (!shift?.id || skipIds.has(shift.id)) continue;
      const existing = await store.get(shift.id);
      const record = {
        ...existing,
        ...shift,
        is_active: typeof shift.is_active === 'boolean' ? shift.is_active : existing?.is_active ?? true,
        created_at: shift.created_at || existing?.created_at || new Date().toISOString(),
        updated_at: shift.updated_at || new Date().toISOString(),
      };
      await store.put(record);
    }

    await tx.done;
  }

  async bulkUpsertTableGroups(groups: Array<RestaurantDB['table_groups']['value']>) {
    if (!this.db || !groups?.length) return;
    const skipIds = await this.getPendingRecordIds('table_groups');
    const tx = this.db.transaction('table_groups', 'readwrite');
    const store = tx.objectStore('table_groups');

    for (const group of groups) {
      if (!group?.id || skipIds.has(group.id)) continue;
      const existing = await store.get(group.id);
      const record = {
        ...existing,
        ...group,
        table_ids: Array.isArray(group.table_ids) ? group.table_ids : existing?.table_ids || [],
        status: group.status || existing?.status || 'active',
        created_at: group.created_at || existing?.created_at || new Date().toISOString(),
        updated_at: group.updated_at || new Date().toISOString(),
      };
      await store.put(record as any);
    }

    await tx.done;
  }

  async bulkUpsertReservations(reservations: Array<RestaurantDB['reservations']['value']>) {
    if (!this.db || !reservations?.length) return;

    const skipIds = await this.getPendingRecordIds('reservations');
    const tx = this.db.transaction('reservations', 'readwrite');
    const store = tx.objectStore('reservations');

    let updated = 0;
    let inserted = 0;
    let skippedNoId = 0;
    let skippedPending = 0;

    for (const reservation of reservations) {
      if (!reservation?.id) {
        skippedNoId++;
        continue;
      }

      // Do not overwrite local pending changes
      if (skipIds.has(reservation.id)) {
        skippedPending++;
        continue;
      }

      const existing = await store.get(reservation.id);

      if (existing) {
        const record = {
          ...existing,
          ...reservation,
          table_ids: Array.isArray(reservation.table_ids)
            ? reservation.table_ids
            : existing?.table_ids ?? (reservation.table_id ? [reservation.table_id] : []),
          created_at: existing.created_at,
          updated_at: reservation.updated_at || new Date().toISOString(),
          notes: reservation.notes ?? existing?.notes ?? undefined,
        };
        await store.put(record as any);
        updated++;
      } else {
        const record = {
          ...reservation,
          table_ids: Array.isArray(reservation.table_ids)
            ? reservation.table_ids
            : reservation.table_id
              ? [reservation.table_id]
              : [],
          created_at: reservation.created_at || new Date().toISOString(),
          updated_at: reservation.updated_at || new Date().toISOString(),
          notes: reservation.notes ?? undefined,
        };
        await store.put(record as any);
        inserted++;
      }
    }

    await tx.done;
    console.log(
      `✅ [LocalDB] bulkUpsertReservations: ${updated} updated, ${inserted} inserted, ${skippedPending + skippedNoId} skipped`
    );
  }

  /**
   * Remove local reservations that no longer exist remotely (and are not pending local sync).
   */
  async pruneMissingReservations(remoteIds: Set<string>) {
    if (!this.db) return { removed: 0, skippedPending: 0 };
    const skipIds = await this.getPendingRecordIds('reservations');
    const tx = this.db.transaction('reservations', 'readwrite');
    const store = tx.objectStore('reservations');

    const keys = await store.getAllKeys();
    let removed = 0;
    let skippedPending = 0;

    for (const key of keys) {
      const id = key as string;
      if (skipIds.has(id)) {
        skippedPending++;
        continue;
      }
      if (!remoteIds.has(id)) {
        await store.delete(id);
        removed++;
      }
    }

    await tx.done;
    if (removed || skippedPending) {
      console.log(`🗑️ [LocalDB] pruneMissingReservations: ${removed} removed, ${skippedPending} skipped (pending)`);
    }
    return { removed, skippedPending };
  }

  async pruneMissingTables(remoteIds: Set<string>) {
    if (!this.db) return { removed: 0, skippedPending: 0 };
    const skipIds = await this.getPendingRecordIds('tables');
    const tx = this.db.transaction('tables', 'readwrite');
    const store = tx.objectStore('tables');

    const keys = await store.getAllKeys();
    let removed = 0;
    let skippedPending = 0;

    for (const key of keys) {
      const id = key as string;
      if (skipIds.has(id)) {
        skippedPending++;
        continue;
      }
      if (!remoteIds.has(id)) {
        await store.delete(id);
        removed++;
      }
    }

    await tx.done;
    return { removed, skippedPending };
  }

  async pruneMissingCustomers(remoteIds: Set<string>) {
    if (!this.db) return { removed: 0, skippedPending: 0 };
    const skipIds = await this.getPendingRecordIds('customers');
    const tx = this.db.transaction('customers', 'readwrite');
    const store = tx.objectStore('customers');

    const keys = await store.getAllKeys();
    let removed = 0;
    let skippedPending = 0;

    for (const key of keys) {
      const id = key as string;
      if (skipIds.has(id)) {
        skippedPending++;
        continue;
      }
      if (!remoteIds.has(id)) {
        await store.delete(id);
        removed++;
      }
    }

    await tx.done;
    if (removed > 0 || skippedPending > 0) {
      console.log(`🗑️ [LocalDB] pruneMissingCustomers: ${removed} removed, ${skippedPending} skipped (pending)`);
    }
    return { removed, skippedPending };
  }

  async pruneMissingTableGroups(remoteIds: Set<string>) {
    if (!this.db) return { removed: 0, skippedPending: 0 };
    const skipIds = await this.getPendingRecordIds('table_groups');
    const tx = this.db.transaction('table_groups', 'readwrite');
    const store = tx.objectStore('table_groups');

    const keys = await store.getAllKeys();
    let removed = 0;
    let skippedPending = 0;

    for (const key of keys) {
      const id = key as string;
      if (skipIds.has(id)) {
        skippedPending++;
        continue;
      }
      if (!remoteIds.has(id)) {
        await store.delete(id);
        removed++;
      }
    }

    await tx.done;
    return { removed, skippedPending };
  }

  private async getPendingRecordIds(tableName: string): Promise<Set<string>> {
    const pending = await this.getPendingSyncItems().catch(() => []);
    const ids = (pending as RestaurantDB['sync_queue']['value'][])
      .filter((item) => item.table_name === tableName)
      .map((item) => item.record_id);
    return new Set(ids);
  }


  // Sync queue operations
  async addToSyncQueue(tableName: string, recordId: string, operation: 'INSERT' | 'UPDATE' | 'DELETE', data?: any): Promise<string> {
    if (!this.db) throw new Error('Database not initialized');
    const tenantId = this.getTenantIdOrThrow();
    
    // Check if there's already a pending sync item for this record
    // If so, update it instead of creating a duplicate
    const pendingItems = await this.db.getAllFromIndex('sync_queue', 'by-status', 'pending');
    const existingItem = pendingItems.find(item => 
      item.table_name === tableName && item.record_id === recordId
    );

    if (existingItem) {
      // Preserve INSERT semantics when a record is created and then edited
      // before it has ever been pushed (e.g. create reservation -> assign table).
      // Downgrading INSERT to UPDATE can fail remotely because the row does not exist yet.
      if (existingItem.operation === 'INSERT' && operation === 'UPDATE') {
        const mergedInsertData = {
          ...(existingItem.data || {}),
          ...(data || {}),
        };
        const updatedInsertItem = {
          ...existingItem,
          operation: 'INSERT' as const,
          data: mergedInsertData,
          created_at: new Date().toISOString(),
        };
        await this.db.put('sync_queue', updatedInsertItem);
        console.log(`🔄 [LocalDB] Kept pending INSERT and merged UPDATE data: ${tableName}:${recordId}`);
        return existingItem.id;
      }

      // Local INSERT followed by local DELETE means there is nothing to sync remotely.
      if (existingItem.operation === 'INSERT' && operation === 'DELETE') {
        await this.db.delete('sync_queue', existingItem.id);
        console.log(`🧹 [LocalDB] Removed pending INSERT after local DELETE: ${tableName}:${recordId}`);
        return existingItem.id;
      }

      // Update existing sync item with new data/operation
      const updatedItem = {
        ...existingItem,
        operation,
        data,
        created_at: new Date().toISOString(), // Update timestamp
      };
      await this.db.put('sync_queue', updatedItem);
      console.log(`🔄 [LocalDB] Updated existing sync queue item instead of creating duplicate: ${tableName}:${recordId}`);
      return existingItem.id;
    }
    
    const id = generateUUID();
    const now = new Date().toISOString();
    const syncEntry = {
      id,
      tenant_id: tenantId,
      table_name: tableName,
      record_id: recordId,
      operation,
      data,
      device_id: this.getDeviceId(),
      created_at: now,
      status: 'pending' as const,
    };

    await this.db.add('sync_queue', syncEntry);
    return id;
  }

  async getPendingSyncItems(): Promise<RestaurantDB['sync_queue']['value'][]> {
    if (!this.db) throw new Error('Database not initialized');
    const allPending = await this.db.getAllFromIndex('sync_queue', 'by-status', 'pending');
    
    // Filter by current tenant_id to ensure tenant isolation
    // Even though each tenant has its own database, this provides an extra safety layer
    if (this.currentTenantId) {
      return allPending.filter(item => item.tenant_id === this.currentTenantId);
    }
    
    // If no tenant is set, return empty array (shouldn't happen in normal flow)
    return [];
  }

  async markSyncItemProcessed(id: string, success: boolean): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const syncItem = await this.db.get('sync_queue', id);
    if (!syncItem) throw new Error('Sync item not found');

    const updatedSyncItem = {
      ...syncItem,
      status: success ? ('completed' as const) : ('failed' as const),
      processed_at: new Date().toISOString(),
    };

    await this.db.put('sync_queue', updatedSyncItem);
  }

  async clearPendingSyncForRecord(tableName: string, recordId: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    const pendingItems = await this.db.getAllFromIndex('sync_queue', 'by-status', 'pending');
    for (const item of pendingItems) {
      if (item.table_name === tableName && item.record_id === recordId) {
        await this.db.delete('sync_queue', item.id);
      }
    }
  }

  async markSyncItemProcessedByRecord(tableName: string, recordId: string, success: boolean): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    // Get all pending sync items for this table and record
    const pendingItems = await this.db.getAllFromIndex('sync_queue', 'by-status', 'pending');
    const syncItem = pendingItems.find(item => 
      item.table_name === tableName && item.record_id === recordId
    );
    
    if (!syncItem) {
      console.warn(`⚠️ Sync item not found for ${tableName}:${recordId} - may have been already processed`);
      return;
    }

    const updatedSyncItem = {
      ...syncItem,
      status: success ? ('completed' as const) : ('failed' as const),
      processed_at: new Date().toISOString(),
    };

    await this.db.put('sync_queue', updatedSyncItem);
  }

  // Utility methods
  private getDeviceId(): string {
    // Generate a unique device ID or use existing one from localStorage
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = generateUUID();
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  }

  async clearDatabase(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    await this.db.clear('customers');
    await this.db.clear('tables');
    await this.db.clear('shifts');
    await this.db.clear('reservations');
    await this.db.clear('table_groups');

    await this.db.clear('sync_queue');
    
    console.log('✅ Local database cleared');
  }

  async clearSyncQueue(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.clear('sync_queue');
    console.log('🧹 Sync queue cleared');
  }

  async closeDatabase(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
      console.log('🔌 Local database closed');
    }
  }

  private async deleteDatabase(name: string): Promise<void> {
    // Delete an IndexedDB database by name (used to purge legacy non-tenant DB)
    await new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve(undefined);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Delete database request blocked'));
    });
  }
}

// Export singleton instance
export const localDatabase = new LocalDatabaseService();
export default localDatabase; 
